/* ==========================================================================
   <ag-collection>: AJAX filtering / sorting / load-more for collection and search pages.
   Every navigation fetches the same section through the Section Rendering API
   (?section_id=...) and swaps it in, then updates the URL with pushState.
   ========================================================================== */
(() => {
  const DENSITY_KEY = 'ag:density';
  let drawerWasOpen = false;
  const cache = new Map();

  class AgCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.drawer = this.querySelector('.ag-filters');
      this.grid = this.querySelector('.ag-grid');
      this.form = this.querySelector('[data-ag-filter-form]');
      this.sort = this.querySelector('[data-ag-sort]');
      this.debounceTimer = null;

      this.addEventListener('click', (event) => this.onClick(event));
      this.form?.addEventListener('input', () => this.onFilterInput());
      this.form?.addEventListener('submit', (event) => event.preventDefault());
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.drawer?.classList.contains('is-open')) this.closeDrawer();
        this.closeSort();
      });
      document.addEventListener('click', this.onDocumentClick);

      this.applyDensity();

      if (drawerWasOpen && this.drawer) {
        this.drawer.classList.add('is-open');
        document.body.classList.add('ag-lock');
      }

      if (!AgCollection.popBound) {
        AgCollection.popBound = true;
        window.addEventListener('popstate', () => {
          const el = document.querySelector('ag-collection');
          if (el) el.load(window.location.href, { push: false });
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    onDocumentClick = (event) => {
      if (this.sort && !this.sort.contains(event.target)) this.closeSort();
    };

    onClick(event) {
      const nav = event.target.closest('a[data-ag-nav]');
      if (nav && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
        event.preventDefault();
        this.load(nav.href);
        return;
      }

      if (event.target.closest('[data-ag-open-filters]')) return this.openDrawer();
      if (event.target.closest('[data-ag-close-filters]')) return this.closeDrawer();

      const density = event.target.closest('[data-ag-density]');
      if (density) {
        this.setDensity(density.dataset.agDensity);
        return;
      }

      const toggle = event.target.closest('.ag-sort__toggle');
      if (toggle) {
        this.sort.classList.contains('is-open') ? this.closeSort() : this.openSort();
        return;
      }

      const option = event.target.closest('.ag-sort__option');
      if (option) {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', option.dataset.value);
        url.searchParams.delete('page');
        this.closeSort();
        this.load(url.toString());
        return;
      }

      const more = event.target.closest('[data-ag-load-more]');
      if (more) this.loadMore(more);
    }

    /* ----- filter drawer ----- */
    openDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      drawerWasOpen = true;
      this.querySelector('[data-ag-open-filters]')?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ag-lock');
      this.lastFocus = document.activeElement;
      window.setTimeout(() => this.drawer.querySelector('.ag-filters__close')?.focus({ preventScroll: true }), 80);
    }

    closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      drawerWasOpen = false;
      this.querySelector('[data-ag-open-filters]')?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ag-lock');
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    onFilterInput() {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => {
        const url = new URL(window.location.href);
        Array.from(url.searchParams.keys()).forEach((key) => {
          if (key.startsWith('filter.')) url.searchParams.delete(key);
        });
        url.searchParams.delete('page');
        new FormData(this.form).forEach((value, key) => {
          if (value !== '' && key.startsWith('filter.')) url.searchParams.append(key, value);
        });
        this.load(url.toString());
      }, 500);
    }

    /* ----- sort ----- */
    openSort() {
      this.sort.classList.add('is-open');
      this.sort.querySelector('.ag-sort__list').hidden = false;
      this.sort.querySelector('.ag-sort__toggle').setAttribute('aria-expanded', 'true');
    }

    closeSort() {
      if (!this.sort) return;
      this.sort.classList.remove('is-open');
      this.sort.querySelector('.ag-sort__list').hidden = true;
      this.sort.querySelector('.ag-sort__toggle').setAttribute('aria-expanded', 'false');
    }

    /* ----- grid density ----- */
    setDensity(mode) {
      try {
        window.sessionStorage.setItem(DENSITY_KEY, mode);
      } catch (e) {
        /* ignore */
      }
      this.applyDensity();
    }

    applyDensity() {
      let mode = 'small';
      try {
        mode = window.sessionStorage.getItem(DENSITY_KEY) || 'small';
      } catch (e) {
        /* ignore */
      }
      this.grid?.classList.toggle('is-large', mode === 'large');
      this.querySelectorAll('[data-ag-density]').forEach((btn) => {
        const active = btn.dataset.agDensity === mode;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    /* ----- fetching ----- */
    sectionUrl(url) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', this.sectionId);
      return u.toString();
    }

    async fetchSection(url) {
      const key = this.sectionUrl(url);
      if (cache.has(key)) return cache.get(key);
      const res = await fetch(key);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      cache.set(key, doc);
      return doc;
    }

    async load(url, { push = true } = {}) {
      this.classList.add('is-loading');
      try {
        const doc = await this.fetchSection(url);
        const next = doc.querySelector('ag-collection');
        if (!next) {
          window.location.href = url;
          return;
        }
        const fresh = document.importNode(next, true);
        // keep the Shopify wrapper's scroll position stable; replace only the component
        this.replaceWith(fresh);
        if (push) window.history.pushState({}, '', url);
        document.dispatchEvent(new CustomEvent('ag:collection:updated'));
        window.AGWishlist?.refreshUI();
      } catch (error) {
        console.error(error);
        window.location.href = url;
      } finally {
        this.classList.remove('is-loading');
      }
    }

    async loadMore(button) {
      const url = button.dataset.nextUrl;
      if (!url) return;
      button.classList.add('is-loading');
      try {
        const doc = await this.fetchSection(url);
        const nextGrid = doc.querySelector('.ag-grid');
        const items = nextGrid ? Array.from(nextGrid.children) : [];
        items.forEach((item) => this.grid.appendChild(document.importNode(item, true)));
        const nextButton = doc.querySelector('[data-ag-load-more]');
        if (nextButton) button.dataset.nextUrl = nextButton.dataset.nextUrl;
        else button.closest('.ag-loadmore')?.remove();
        window.AGWishlist?.refreshUI();
      } catch (error) {
        console.error(error);
      } finally {
        button.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('ag-collection')) customElements.define('ag-collection', AgCollection);
})();
